import React from "react";

import { Box, BoxSx } from "../../foundations";
import { Text } from "../../foundations/Typography/Text";
import { useTooltipVisibility } from "../../hooks/useTooltipVisibility";
import { getTooltipStyles } from "./TooltipStyles";

export type ArrowPosition =
  | "left-top"
  | "left-center"
  | "left-bottom"
  | "top-start"
  | "top-center"
  | "top-end"
  | "right-top"
  | "right-center"
  | "right-bottom"
  | "bottom-start"
  | "bottom-center"
  | "bottom-end";

export interface TooltipProps {
  children: React.ReactNode;
  content: string | React.ReactNode;
  arrowPosition?: ArrowPosition;
}

const contentStyles: BoxSx = {
  position: "relative",
  backgroundColor: "surface-neutral-default",
  borderRadius: "radius-2",
  paddingY: "spacing-2",
  paddingX: "spacing-3",
  lineHeight: "leading-2"
};

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  arrowPosition = "top-center"
}) => {
  const { visible, showTooltip, hideTooltip } = useTooltipVisibility();
  const tooltipId = React.useId();

  const tooltipStyles = React.useMemo(
    () => getTooltipStyles(arrowPosition),
    [arrowPosition]
  );

  return (
    <Box
      tabIndex={0}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      onTouchStart={showTooltip}
      onTouchEnd={hideTooltip}
      sx={{ position: "relative" }}
    >
      <Box aria-describedby={visible ? tooltipId : undefined}>{children}</Box>
      {visible && (
        <Box
          id={tooltipId}
          role="tooltip"
          aria-hidden={!visible}
          sx={tooltipStyles}
        >
          <Box sx={contentStyles}>
            <Text variant="body-2-regular">{content}</Text>
          </Box>
        </Box>
      )}
    </Box>
  );
};
